import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getAllgateway } from "@/services/gatewayService";

export default function ExquraPackages({ fields = {} }) {
  const [fetchpkg, setPkg] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingPkg(true);

      try {
        // Same source (and admin-controlled order) as the full "Golf Getaways"
        // listing page — just the first three. getPackagesByRounds() used to
        // hard-filter on rounds ∈ {1,3,5}, which silently dropped custom
        // packages and ignored the admin's drag order.
        const result = await getAllgateway();
        setPkg(Array.isArray(result) ? result.slice(0, 3) : []);
      } catch (err) {
        console.error("Failed to load packages:", err);
        setPkg([]);
      } finally {
        setLoadingPkg(false);
      }
    })();
  }, []);

  const packages = fetchpkg.map((pkg) => ({
    id: pkg.id,
    title: pkg.destination,
    tagline: pkg.tagline,
    duration: pkg.duration,
    rounds: pkg.rounds,
    nights: pkg.nights,
    custom: pkg.custom,
    features: pkg.included?.slice(0, 5) || [],
    price: pkg.price != null ? `SGD ${pkg.price}` : "—",
    image: pkg.image,
  }));

  const {
    headline = "EXQURA GOLF — YOUR PREMIER CONCIERGE FOR GOLFING EXPERIENCES",
    subtext = "We handle the details, you focus on the game",
    sectionLabel = "Golf Getaways",
    linkLabel = "View all Golf Getaways",
  } = fields;
  const navigate = useNavigate();
  return (
    <section className="bg-white pt-32 pb-20 relative z-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-black font-bold text-2xl sm:text-3xl lg:text-4xl max-w-4xl mx-auto leading-tight uppercase tracking-wide whitespace-pre-line">
            {headline}
          </h2>
          <p className="text-[#666666] mt-4 text-base sm:text-lg whitespace-pre-line">{subtext}</p>
        </div>

        {/* Section Label */}
        <div className="mb-8 border-b border-gray-100 pb-4">
          <h3 className="text-[#888888] font-semibold text-sm tracking-widest uppercase">
            {sectionLabel}
          </h3>
        </div>

        {/* Cards Grid */}
        {loadingPkg ? (
          <div className="min-h-screen bg-[#0B2421] flex items-center justify-center">
            <div className="text-white/60 text-lg">Loading...</div>
          </div>
        ) : packages.length === 0 ? (
          <p className="text-center text-[#888888] py-12">
            No golf getaways are available right now — check back soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg, i) => (
              <div
                key={i}
                className="bg-[#F7F7F4] rounded-2xl overflow-hidden shadow-sm border border-gray-100 group flex flex-col"
              >
                {/* Image — full width, on top */}
                <div className="aspect-[4/3] shrink-0 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                {/* Content — below the image */}
                <div className="p-6 flex-1 flex flex-col">
                  <h4 className="text-[#0B2421] font-bold text-2xl mb-1">
                    {pkg.title}
                  </h4>
                  <p className="text-[#666666] text-base mb-4">
                    {pkg.duration}
                  </p>
                  <ul className="space-y-3 mb-4 flex-1">
                    {pkg.features.map((f, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-[#0B2421]"
                      >
                        <Check className="w-5 h-5 text-[#0B2421] shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-[#888888]">From</p>
                    <div className="flex items-baseline gap-1 mb-4">
                      <p className="text-[#0B2421] font-bold text-3xl">
                        {pkg.price}
                      </p>
                      <p className="text-sm text-[#888888]">Per Person</p>
                    </div>
                    <button
                      onClick={() => navigate(`/package-detail?id=${pkg.id}`)}
                      className="w-full bg-[#0B2421] hover:bg-[#0B2421]/90 text-white font-semibold py-3 rounded-xl text-sm transition-colors"
                    >
                      Explore
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View all — right-aligned text link */}
        <div className="mt-8 flex justify-end">
          <Link
            to="/golf-getaways"
            className="inline-flex items-center gap-1.5 text-[#0B2421] font-semibold text-sm hover:underline underline-offset-4 transition-colors"
          >
            {linkLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
